// US2028928: Modern UI soft-launch admin toggle.
//
// Rendered inside the Global Toggles tab for launch admins only. Reuses the
// same slider the profile feature flags use (SelfServiceFeatureToggle) so the
// control looks and behaves like the One Page Summary / Modern UI toggles.
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { FaSync } from "react-icons/fa";
import SelfServiceFeatureToggle from "../Profile/SelfServiceFeatureToggle";
import {
  fetchModernUILaunchStatus,
  setModernUILaunchStatus,
} from "../../connections/ModernUILaunch";

// Synthetic "feature" describing the global launch toggle, shaped for
// SelfServiceFeatureToggle.
const LAUNCH_FEATURE = {
  name: "modern_ui_default_on",
  displayName: "Modern UI Default (Soft Launch)",
  description:
    "When ON, users who haven't chosen are defaulted to the Modern UI. MIMs are excluded. Users can still toggle it in their profile.",
  category: "launch",
  beta: false,
  experimental: false,
  dependencies: [],
};

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/**
 * ModernUILaunchToggle — launch-admin control for the modern_ui_default_on
 * global toggle. Renders null for non-launch-admins (defence in depth; the
 * backend independently enforces the allowlist on every call).
 *
 * @param {Object} props
 * @param {boolean} props.isLaunchAdmin - modern_ui_launch_admin flag from session
 */
function ModernUILaunchToggle({ isLaunchAdmin }) {
  if (!isLaunchAdmin) {
    return null;
  }
  return <ModernUILaunchToggleInner />;
}

function ModernUILaunchToggleInner() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // { enabled, updatedAt, updatedBy }
  const [error, setError] = useState(null); // load error
  const [actionError, setActionError] = useState(null); // PATCH error
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchModernUILaunchStatus({ signal: controller.signal });
      setStatus(data);
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  // SelfServiceFeatureToggle calls onChange(featureName, newValue) and manages
  // its own in-flight spinner. Throwing on failure lets it stop the spinner;
  // the controlled `enabled` prop stays unchanged so the switch visually reverts.
  const handleChange = useCallback(async (_name, newValue) => {
    setActionError(null);
    try {
      const updated = await setModernUILaunchStatus(newValue);
      setStatus(updated);
    } catch (err) {
      setActionError(
        err.status === 403
          ? "Access denied. You do not have permission to change this setting."
          : err.message || "Failed to update setting. Please try again."
      );
      throw err;
    }
  }, []);

  if (loading) {
    return (
      <div className="d-flex align-items-center py-3" aria-live="polite">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading Modern UI launch status...
      </div>
    );
  }

  if (error) {
    const is403 = error.status === 403;
    return (
      <Alert variant="danger" role="alert">
        <Alert.Heading>
          {is403 ? "Access Denied" : "Error Loading Status"}
        </Alert.Heading>
        <p className="mb-0">
          {is403
            ? "You do not have permission to view the Modern UI launch setting."
            : error.message || String(error)}
        </p>
        {!is403 && (
          <Button variant="outline-danger" size="sm" className="mt-2" onClick={load}>
            <FaSync className="me-1" aria-hidden="true" /> Try Again
          </Button>
        )}
      </Alert>
    );
  }

  const updatedAt = formatDate(status?.updatedAt);
  const updatedBy = status?.updatedBy || null;

  return (
    <div className="mb-4">
      <h2 className="h5 mb-1">Modern UI Launch</h2>
      <p className="text-muted mb-3">
        Soft-launch default for all users. Only launch admins can change this.
      </p>

      {actionError && (
        <Alert variant="danger" dismissible onClose={() => setActionError(null)} role="alert">
          {actionError}
        </Alert>
      )}

      <SelfServiceFeatureToggle
        feature={LAUNCH_FEATURE}
        enabled={Boolean(status?.enabled)}
        onChange={handleChange}
      />

      {(updatedBy || updatedAt) && (
        <p className="text-muted small mb-0">
          Last changed
          {updatedBy ? ` by ${updatedBy}` : ""}
          {updatedAt ? ` at ${updatedAt}` : ""}
        </p>
      )}
    </div>
  );
}

export default ModernUILaunchToggle;
